import type { AttachmentEntityKind, AttachmentListResponse } from '@palaccount/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api } from '@/api';
import { throwApiError } from '@/lib/server-messages';

/**
 * INV-H5 / IT-S8 — the files on one record. Every write answers with the
 * whole list (the contract's promise), so the panel replaces its cache and
 * never merges — the favourites bar's model.
 */
export const attachmentsKey = (kind: AttachmentEntityKind, entityId: string) =>
  ['attachments', kind, entityId] as const;

export function useAttachments(kind: AttachmentEntityKind, entityId: string | undefined) {
  return useQuery({
    queryKey: attachmentsKey(kind, entityId ?? ''),
    enabled: entityId !== undefined && entityId !== '',
    queryFn: async () => {
      const res = await api.attachments.list({ params: { kind, entityId: entityId ?? '' } });
      if (res.status !== 200) throwApiError(res.status, res.body);
      return res.body;
    },
  });
}

function useReplaceList(kind: AttachmentEntityKind, entityId: string) {
  const queryClient = useQueryClient();
  return (list: AttachmentListResponse) =>
    queryClient.setQueryData(attachmentsKey(kind, entityId), list);
}

/** One file per request: the server caps each at ATTACHMENT_MAX_FILE_BYTES. */
export function useUploadAttachment(kind: AttachmentEntityKind, entityId: string) {
  const replace = useReplaceList(kind, entityId);
  return useMutation({
    mutationFn: async (file: File) => {
      const res = await api.attachments.upload({ params: { kind, entityId }, body: { file } });
      if (res.status !== 201) throwApiError(res.status, res.body);
      return res.body;
    },
    onSuccess: replace,
  });
}

export function useRemoveAttachment(kind: AttachmentEntityKind, entityId: string) {
  const replace = useReplaceList(kind, entityId);
  return useMutation({
    mutationFn: async (id: string) => {
      const res = await api.attachments.remove({ params: { id }, body: {} });
      if (res.status !== 200) throwApiError(res.status, res.body);
      return res.body;
    },
    onSuccess: replace,
  });
}

/**
 * The bytes, as a plain URL: a link lets the session cookie ride along and
 * the browser's own download UI take over, and an <img> can draw a thumbnail
 * from the same address — the documents route's reasoning.
 */
export function attachmentUrl(id: string): string {
  return `/api/attachments/${id}/content`;
}

/**
 * A size a person can read. Latin digits in both languages — figures are LTR
 * islands (PART 4 §4.10), and a file size is a figure.
 */
export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(bytes < 10 * 1024 ? 1 : 0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}
